import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { 
  PartyPopper, 
  Calendar, 
  Clock, 
  MapPin, 
  Ticket,
  Loader2,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event Card Component
const EventCard = ({ event, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -4 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/10 hover:border-orange-500/50 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/10"
  >
    {/* Image */}
    <div className="relative aspect-[16/9] bg-gradient-to-br from-orange-600 to-yellow-600 overflow-hidden">
      {event.flyer_url ? (
        <img 
          src={event.flyer_url} 
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <PartyPopper className="w-16 h-16 text-white/30" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      
      {/* Badge */}
      <div className="absolute top-3 left-3">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/90 text-white text-xs font-medium">
          <PartyPopper className="w-3 h-3" />
          Festival
        </span>
      </div>
    </div>
    
    <div className="p-5">
      <h3 className="font-bold text-white text-lg mb-2 group-hover:text-orange-400 transition-colors line-clamp-1">
        {event.name}
      </h3>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Calendar className="w-4 h-4 text-orange-500" />
          <span>{event.date}</span>
          <Clock className="w-4 h-4 text-orange-500 ml-2" />
          <span>{event.time}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <MapPin className="w-4 h-4" />
          <span className="truncate">{event.venue}</span>
        </div>
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div>
          <p className="text-xs text-gray-500">From</p>
          <p className="text-xl font-bold text-orange-500">D{event.ticket_price}</p>
        </div>
        <Button className="bg-gradient-to-r from-orange-600 to-yellow-600 hover:from-orange-500 hover:to-yellow-500 text-white">
          <Ticket className="w-4 h-4 mr-2" />
          Get Tickets
        </Button>
      </div>
    </div>
  </motion.div>
);

export default function FestivalsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFestivals = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/events/public?type=festival`);
        setEvents(response.data.filter(e => e.event_type === 'festival'));
      } catch (error) {
        console.error('Failed to fetch festivals:', error);
        // Fallback to all events and filter
        try {
          const response = await axios.get(`${API_URL}/api/events/public`);
          setEvents(response.data.filter(e => e.event_type === 'festival'));
        } catch (e) {
          console.error('Fallback also failed:', e);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchFestivals();
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/30 mb-6">
              <PartyPopper className="w-5 h-5 text-orange-500" />
              <span className="text-orange-500 font-medium">Festival Tickets</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Festivals.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Celebrate Together.</span>
            </h1>
            
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Experience the best festivals, from music to food to cultural celebrations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16 bg-[#0F0F0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Upcoming Festivals</h2>
              <p className="text-gray-400 mt-1">The best festival experiences await</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : events.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <EventCard
                    event={event}
                    onClick={() => navigate(`/event/${event.id}`)}
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <PartyPopper className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Festivals Available</h3>
              <p className="text-gray-400 mb-6">Check back later for upcoming festivals.</p>
              <Link to="/">
                <Button className="bg-gradient-to-r from-orange-600 to-yellow-600">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Browse All Events
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0F0F0F] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500">© 2026 Gam Ticket. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
